import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

/**
 * Layout route guard — mirrors the pattern used by AccountLayout.
 *
 * Wrap any route that requires authentication:
 *
 *   <Route element={<RequireAuth />}>
 *     <Route path="/some/protected" element={<SomePage />} />
 *   </Route>
 *
 * On redirect, the current path + search string is preserved in ?next=
 * so Auth.tsx can return the user here after login.
 */
export function RequireAuth() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/auth?next=${next}`, { replace: true });
    }
  }, [user, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <Outlet />;
}
